import { useContext, useEffect, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/Accordion';
import { Checkbox } from 'components/Checkbox';
import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { InputValue } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import {
  LoadDateTimeInput,
  LoadSelectInput,
  LoadTextInput,
} from 'pages/LoadView/LoadInformation/Components';
import { LoadStopTypes, NormalizedLoad, TMSLocation } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import {
  LoadStopNestedSection,
  useEnsureNestedSectionOpenOnSuggestion,
} from 'utils/expandStopDetailsOnSuggestion';
import { datetimeFieldOptions } from 'utils/formValidators';

import { ThreeGTwoStopFields } from '../Components/ThreeG/ThreeG';

export function PickupSectionForm({
  suggestedFieldsPrevValues,
  originalSuggestionData,
  formMethods,
  isLoadingLocations,
  locations,
  handleRefreshLocations,
  setLocations,
}: {
  suggestedFieldsPrevValues: any;
  originalSuggestionData?: NormalizedLoad;
  formMethods: UseFormReturn<NormalizedLoad>;
  isLoadingLocations: boolean;
  locations: Maybe<TMSLocation[]>;
  handleRefreshLocations: () => void;
  setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
}) {
  const stop = LoadStopTypes.Pickup;

  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = formMethods;

  const fieldAttrs = useFieldAttributes();
  const apptRequiredAttr = getFieldAttribute(fieldAttrs, 'pickup.apptRequired');
  const apptConfirmedAttr = getFieldAttribute(
    fieldAttrs,
    'pickup.apptConfirmed'
  );
  const apptNumAttr = getFieldAttribute(fieldAttrs, 'pickup.apptNum');
  const refNumberAttr = getFieldAttribute(fieldAttrs, 'pickup.refNumber');

  const watchedLocationID = watch(`${stop}.externalTMSID`);
  const watchedLocationObj = watch(stop);

  const { tmsName, tmsID } = useTMSContext();
  const { aiDefaultValues } = useExtendedFormContext();
  const [isMcleodTMS, setIsMcleodTMS] = useState<boolean>(
    tmsName === TMS.McleodEnterprise
  );

  useEffect(() => {
    if (isMcleodTMS) {
      setIsMcleodTMS(true);
    }
  }, [tmsName]);

  useEffect(() => {
    if (watchedLocationID) {
      const selectedLoc = locations?.find(
        (loc) => loc.externalTMSID === watchedLocationID
      );
      if (!selectedLoc) {
        return;
      }
      // setLocations(injectSelectedObject(selectedLoc, locations ?? []));

      Object.entries(selectedLoc).forEach(([key, value]) => {
        setValue(`${stop}.${key}` as FieldPath<NormalizedLoad>, value, {
          shouldDirty: aiDefaultValues,
        });
      });
    }
  }, [watchedLocationID, locations, aiDefaultValues, setValue]);

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);
  const [openAppointmentSections, setOpenAppointmentSections] =
    useEnsureNestedSectionOpenOnSuggestion(
      clickedSuggestion,
      LoadStopTypes.Pickup,
      LoadStopNestedSection.PickupAppointment
    );

  const currentLoad = formMethods.watch(); // Get current form values for all fields

  const isApptRequirementSupported =
    apptRequiredAttr && !apptRequiredAttr?.isNotSupported;
  const isApptRequired = watchedLocationObj?.apptRequired;
  // Show appt required field if it's supported and either required or from GlobalTranzTMS
  const shouldShowApptRequired =
    isApptRequirementSupported && (isApptRequired || TMS.GlobalTranzTMS);

  const isApptConfirmed = watchedLocationObj?.apptConfirmed;
  return (
    <>
      {!isMcleodTMS ? (
        <LoadTextInput name='pickup.name' label='Name' />
      ) : (
        <>
          <RHFLocationSelect
            required={true}
            name={`${stop}.externalTMSID`}
            label='Name'
            control={control}
            errors={errors}
            locations={locations}
            setLocations={setLocations}
            isLoadingLocations={isLoadingLocations}
            handleRefreshLocations={handleRefreshLocations}
            tmsID={tmsID}
          />
        </>
      )}

      <Accordion type='multiple' className='w-full'>
        <AccordionItem value='pickup-address'>
          <AccordionTrigger className='text-sm font-medium py-2'>
            Address & Contact Info
          </AccordionTrigger>
          <AccordionContent className='space-y-3 pt-2'>
            <LoadTextInput name='pickup.addressLine1' label='Address Line 1' />
            <LoadTextInput name='pickup.addressLine2' label='Address Line 2' />
            <LoadTextInput name='pickup.city' label='City' />
            <LoadTextInput name='pickup.state' label='State' />
            <LoadTextInput
              name='pickup.zipCode'
              label='Zip Code'
              placeholder='12345'
              // options={zipCodeFieldOptions}
            />
            <LoadTextInput name='pickup.country' label='Country' />
            <LoadTextInput name='pickup.contact' label='Contact' />
            <LoadTextInput
              name='pickup.phone'
              label='Phone'
              placeholder='(555) 555-5555'
              // options={phoneFieldOptions}
              inputValue={InputValue.PHONE_NUMBER}
            />
            <LoadTextInput
              name='pickup.email'
              label='Email'
              placeholder='example@email.com'
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Accordion
        type='multiple'
        className='w-full'
        value={openAppointmentSections}
        onValueChange={setOpenAppointmentSections}
      >
        <AccordionItem value={LoadStopNestedSection.PickupAppointment}>
          <AccordionTrigger className='text-sm font-medium py-2'>
            Appointment Info
          </AccordionTrigger>
          <AccordionContent className='pt-2'>
            <Flex direction='col' gap='md'>
              {tmsName === TMS.ThreeG ? (
                <ThreeGTwoStopFields
                  isPickup={true}
                  currentLoad={currentLoad}
                />
              ) : (
                <>
                  <LoadTextInput
                    name='pickup.businessHours'
                    label='Business Hours'
                  />
                  {!refNumberAttr?.isNotSupported && (
                    <LoadRefPillInput
                      name='pickup.refNumber'
                      label='Ref #'
                      control={control}
                      originalSuggestionData={
                        originalSuggestionData || getValues()
                      }
                      disabled={Boolean(refNumberAttr?.isReadOnly)}
                      pillDisplayContext='loadView'
                      highlightNewValuesInLoadView={true}
                    />
                  )}
                  <LoadDateTimeInput
                    name='pickup.readyTime'
                    label='Ready Time'
                    options={datetimeFieldOptions}
                    load={currentLoad}
                  />

                  {/*
                    GlobalTranzTMS doesn't allow changes to appointment type directly,
                    but it's rather inferred through confirmation numbers and
                    whether it's required or not.
                  */}
                  {tmsName === TMS.GlobalTranzTMS && (
                    <LoadTextInput
                      name='pickup.apptType'
                      label='Appointment Status'
                      options={{ disabled: true }}
                    />
                  )}

                  {shouldShowApptRequired && (
                    <Controller
                      name={`${stop}.apptRequired`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          label='Appointment Required?'
                          labelClassName='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                          checked={field.value ?? false}
                          onCheckedChange={(checked) => field.onChange(checked)}
                          disabled={apptRequiredAttr?.isReadOnly}
                        />
                      )}
                    />
                  )}

                  <Controller
                    name={`${stop}.apptConfirmed`}
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        label='Confirm Appointment'
                        labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                        checked={field.value ?? false}
                        onCheckedChange={(checked) => field.onChange(checked)}
                        disabled={apptConfirmedAttr?.isReadOnly}
                      />
                    )}
                  />

                  {/* Allow GlobalTranzTMS users to set appointment number, if appt is confirmed */}
                  {tmsName === TMS.GlobalTranzTMS && isApptConfirmed && (
                    <LoadTextInput
                      name='pickup.apptNum'
                      label='Appointment Number'
                      placeholder='APPT1234'
                      readOnly={apptNumAttr?.isReadOnly}
                    />
                  )}

                  <LoadDateTimeInput
                    name='pickup.apptStartTime'
                    label='Appointment Start Time'
                    options={datetimeFieldOptions}
                    prevValue={suggestedFieldsPrevValues?.pickupApptTime}
                    load={currentLoad}
                  />
                  <LoadDateTimeInput
                    name='pickup.apptEndTime'
                    label='Appointment End Time'
                    options={datetimeFieldOptions}
                    load={currentLoad}
                  />

                  {tmsName === TMS.Turvo && (
                    <LoadSelectInput
                      name='pickup.apptType'
                      label='Appointment Type'
                      options={[
                        { value: 'FCFS', label: 'FCFS' },
                        { value: 'By appointment', label: 'By appointment' },
                      ]}
                    />
                  )}

                  <LoadTextInput
                    name='pickup.apptNote'
                    label='Appointment Note'
                  />
                </>
              )}
            </Flex>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
}
